import React, { useEffect, useState } from 'react';
import { FormGroup, Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FlexCol from '../Common/FlexCol';
import { Flex, notification } from 'antd';
import DateSelect from '../Common/DateSelector';
import FormInput from '../Common/FormInput';
import CommonButton from '../Common/Button';
import MultiSelector from '../Common/MultiSelector';
import API, { action } from '../../Api';
import Label from '../Common/Label';
import DropdownButton from '../Common/DropdownButton';
import { MdOutlineDeleteOutline } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';

function MultipleMilkEntries({ isOpen, toggle, selectedCattle }) {
    const [activeTab, setActiveTab] = useState('Heat');
    const tabs = ['morning', 'evening', 'both'];
    const [cattleid, setCattleid] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const id = localStorage.getItem('userid');
    const [loading, setLoading] = useState(false);
    const [cattleEntries, setCattleEntries] = useState([
        { id: '', quantity: '' }
    ]);

    const isDisabled = !!selectedCattle;

    const handleAddCattleEntry = () => {
        const newEntry = { id: '', quantity: '' };
        const updatedEntries = [...cattleEntries, newEntry];
        setCattleEntries(updatedEntries);
        setSearchKey("")
        formik.setFieldValue("cattleEntries", updatedEntries);
    };

    const handleEntriesChange = (index, field, value) => {
        const updatedEntries = [...cattleEntries];
        updatedEntries[index][field] = value;
        setCattleEntries(updatedEntries);
        formik.setFieldValue("cattleEntries", updatedEntries);

        // Fix: Only set field touched for 'id' if the value is empty (to avoid showing error on search/select)
        if (field === 'id') {
            if (!value) {
                formik.setFieldTouched(`cattleEntries.${index}.${field}`, true);
            } else {
                formik.setFieldTouched(`cattleEntries.${index}.${field}`, false);
            }
        } else {
            formik.setFieldTouched(`cattleEntries.${index}.${field}`, true);
        }
    };

    const handleDeleteEntry = (indexToDelete) => {
        if (cattleEntries.length > 1) {
            const updatedEntries = cattleEntries.filter((_, index) => index !== indexToDelete);
            setCattleEntries(updatedEntries);
            formik.setFieldValue("cattleEntries", updatedEntries);
        }
    };

    const getFilteredCattleOptions = (currentIndex) => {
        const selectedIds = cattleEntries
            .filter((entry, idx) => idx !== currentIndex && entry.id)
            .map(entry => entry.id);

        return cattleid.filter(option => !selectedIds.includes(option.value))
            .map(option => ({
                ...option,
                label: option.label.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
            }));
    };

    // Fixed Formik setup with proper validation
    const formik = useFormik({
        initialValues: {
            milkingSlot: 'morning',
            milkingDate: '',
            cattleEntries: selectedCattle && selectedCattle.length > 0 
                ? [{ id: selectedCattle[0].CattleID, quantity: selectedCattle[0].quantity || '' }]
                : [{ id: '', quantity: '' }]
        },
        validationSchema: Yup.object({
            milkingDate: Yup.date().required('Milking Date is required'),
            milkingSlot: Yup.string().required('Milking slot is required'),
            cattleEntries: Yup.array().of(
                Yup.object().shape({
                    id: Yup.string().required('Cattle  is required'),
                    quantity: Yup.number()
                        .required('Quantity is required')
                        .positive('Quantity must be greater than zero')
                        .min(0.1, 'Quantity must be at least 0.1')
                        .max(9999, 'Quantity cannot exceed 9999')
                })
            ).min(1, 'At least one cattle entry is required')
        }),
        onSubmit: async (values) => {
            console.log(values, "values");
            setLoading(true);
            try {
                await action(API.ADD_MILKING, {
                    cattle_id: values.cattleEntries,
                    milking_date: values.milkingDate,
                    slot: values.milkingSlot,
                    created_by: id
                });
                notification.success({ 
                    message: "Record milk added successfully", 
                    placement: "top" 
                });

                formik.resetForm();
                setCattleEntries([{ id: '', quantity: '' }]);
                toggle();
            } catch (error) {
                console.error(error);
                notification.error({ 
                    message: error.response?.data?.response?.message || 'An error occurred', 
                    placement: "top" 
                });
            } finally {
                setLoading(false);
            }
        },
    });

    const toggle1 = () => {
        formik.resetForm();
        setSearchKey("")
        setCattleEntries([{ id: '', quantity: '' }]);
        toggle();
    };

    useEffect(() => {
        if (selectedCattle && selectedCattle.length > 0) {
            const initialEntries = [{ 
                id: selectedCattle[0].CattleID || '', 
                quantity: selectedCattle[0].quantity || '' 
            }];
            
            formik.setFieldValue("milkingSlot", selectedCattle[0].slot || 'morning');
            formik.setFieldValue("milkingDate", selectedCattle[0].milking_date || "");
            formik.setFieldValue("cattleEntries", initialEntries);
            setCattleEntries(initialEntries);
        }
    }, [selectedCattle]);

    const getCattleId = async () => {
        try {
            const result = await action(API.GET_CATTLE_FILTER_GENDER, {
                searchKey: searchKey,
                page: 1,
                pageCount: 60,
                type: 3
            });
            const cattleID = result.response.data.map(id => ({
                label: id.cattle_id,
                value: id.cattle_id
            }));
            console.log(result, "result");
            setCattleid(cattleID);
        } catch (error) {
            console.error("Error fetching cattle data:", error);
        }
    };

    useEffect(() => {
        getCattleId();
    }, [searchKey]);

    return (
        <Offcanvas isOpen={isOpen} toggle={toggle1} direction="end" className="w-full md:!w-[400px] 2xl:!w-[480px] max-h-full overflow-y-auto relative">
            <button
                type="button"
                onClick={toggle1}
                aria-label="Close"
                style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1001,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <IoMdClose size={18} color="#333" />
            </button>
            <FlexCol className={"p-4"}>
                <OffcanvasHeader>
                    <div>
                        <div className="text-[16px] font-semibold 2xl:text-2xl text-[#09090B] leading-[21.6px] break-words font-plus-jakarta-sans">
                            Record Milk
                        </div>
                        <div className="text-[10px] 2xl:text-sm text-[#726C6C] font-plus-jakarta-sans">
                            Log Daily Milk Production
                        </div>
                    </div>
                </OffcanvasHeader>
                <OffcanvasBody>
                    <form onSubmit={formik.handleSubmit}>
                        <FormGroup>
                            {!isDisabled ? (
                                <FlexCol className={""} gap={20}>
                                    <Flex className="grid grid-cols-1 gap-[10px] items-center">
                                        <div style={{ flexDirection: 'column', justifyContent: 'flex-start', gap: 14, display: 'flex' }}>
                                            <div className="text-[#16A34A] text-[16px] md:text-[12px] 2xl:text-[16px] font-medium leading-[19.2px] break-words font-plus-jakarta-sans">
                                                Milking Slot
                                            </div>
                                            <div className="flex gap-2">
                                                {tabs.map((tab) => (
                                                    <div
                                                        key={tab}
                                                        onClick={() => !isDisabled && formik.setFieldValue('milkingSlot', tab)}
                                                        className={`p-2 rounded-lg cursor-pointer ${formik.values.milkingSlot === tab ? 'bg-[#E3E3E3]' : 'bg-transparent'} flex justify-center items-center`}
                                                    >
                                                        <div className={`text-[#0A0B0A] text-[12px] md:text-[10px] font-medium leading-[16.8px] break-words capitalize font-plus-jakarta-sans`}>
                                                            {tab}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <DateSelect
                                            title="Milking Date"
                                            placeholder="Choose Milking Date"
                                            value={formik.values.milkingDate}
                                            change={value => formik.setFieldValue('milkingDate', value)}
                                            error={formik.touched.milkingDate && formik.errors.milkingDate}
                                            disabled={isDisabled}
                                            required={true}
                                            disabledFutureDate={true}
                                        />

                                        {/* Add space between Milking Date and Select Cattle */}
                                        <div className="h-4"></div>

                                        {selectedCattle && selectedCattle.length > 0 ? (
                                            <>
                                                <FormInput
                                                    title="Select Cattle"
                                                    placeholder="1865"
                                                    value={selectedCattle[0].CattleID}
                                                    onChange={value => formik.setFieldValue('cattleEntries', [{ id: value, quantity: formik.values.cattleEntries[0]?.quantity || '' }])}
                                                    disabled="true"
                                                />
                                                <FormInput
                                                    title="Quantity"
                                                    placeholder="Enter Quantity"
                                                    type='number'
                                                    value={formik.values.cattleEntries[0]?.quantity || ''}
                                                    change={(value) => {
                                                        const updatedEntries = [...formik.values.cattleEntries];
                                                        updatedEntries[0] = { ...updatedEntries[0], quantity: value };
                                                        formik.setFieldValue('cattleEntries', updatedEntries);
                                                    }}
                                                    error={formik.touched.cattleEntries && formik.errors.cattleEntries?.[0]?.quantity}
                                                    disabled={isDisabled}
                                                    required={true}
                                                    maxLength={4}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {cattleEntries.map((entry, index) => {
                                                    // Only show error if the field is touched and the value is empty
                                                    const showCattleError =
                                                        formik.touched.cattleEntries &&
                                                        formik.touched.cattleEntries[index] &&
                                                        formik.touched.cattleEntries[index].id &&
                                                        formik.errors.cattleEntries &&
                                                        formik.errors.cattleEntries[index] &&
                                                        formik.errors.cattleEntries[index].id &&
                                                        !entry.id;

                                                    return (
                                                        <div key={index}>
                                                            {/* Select Cattle on its own line - decreased width to match quantity input */}
                                                            <div className="w-full mb-2">
                                                                <DropdownButton
                                                                    title="Select Cattle"
                                                                    placeholder="Select Cattle"
                                                                    options={getFilteredCattleOptions(index)}
                                                                    value={entry.id}
                                                                    onSearch={setSearchKey}
                                                                    onDropdownVisibleChange={(visible) => {
                                                                        if (!visible) {
                                                                            setSearchKey('');
                                                                        }
                                                                    }}
                                                                    required={true}
                                                                    change={(value) => handleEntriesChange(index, 'id', value)}
                                                                    error={showCattleError ? formik.errors.cattleEntries[index].id : undefined}
                                                                    searchKey={setSearchKey}
                                                                />
                                                            </div>
                                                            
                                                            {/* Quantity and Add/Remove button on same line */}
                                                            <div className="flex gap-4 items-start">
                                                                <div className="w-[90%] flex flex-col">
                                                                    <FormInput
                                                                        title="Quantity"
                                                                        placeholder="Enter Quantity"
                                                                        placeholderStyle="text-xs"
                                                                        type="number"
                                                                        value={entry.quantity}
                                                                        change={(value) => handleEntriesChange(index, 'quantity', value)}
                                                                        error={formik.touched.cattleEntries?.[index]?.quantity && formik.errors.cattleEntries?.[index]?.quantity}
                                                                        required={true}
                                                                        maxLength={4}
                                                                    />
                                                                </div>
                                                                <div className="flex items-center h-[40px] mt-6">
                                                                    {index === 0 ? (
                                                                        <CommonButton
                                                                            title="+"
                                                                            onClick={handleAddCattleEntry}
                                                                            style={{ fontSize: "10px" }}
                                                                        />
                                                                    ) : (
                                                                        <CommonButton
                                                                            title="-"
                                                                            onClick={() => handleDeleteEntry(index)}
                                                                            style={{ fontSize: "10px", backgroundColor: "red" }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </Flex>
                                </FlexCol>
                            ) : (
                                <>
                                    <Label title='Milking Slot' value={formik.values.milkingSlot} />
                                    <Label title='Milking Date' value={formik.values.milkingDate} />
                                    <Label title='Selected Cattle' value={selectedCattle?.[0]?.CattleID} />
                                    <Label title='Quantity' value={formik.values.cattleEntries[0]?.quantity} />
                                </>
                            )}
                        </FormGroup>

                        <div className="flex justify-between mt-8">
                            {isDisabled ? (
                                <CommonButton
                                    style={{ fontSize: "12px", backgroundColor: "none", color: "#726C6C", border: "1px #CCCBCB solid ", marginRight: "8px" }}
                                    onClick={toggle1}
                                    title="Cancel"
                                />
                            ) : (
                                <>
                                    <CommonButton
                                        style={{ fontSize: "12px", backgroundColor: "none", color: "#726C6C", border: "1px #CCCBCB solid ", marginRight: "8px" }}
                                        onClick={toggle1}
                                        outline
                                        title="Discard"
                                        disabled={loading}
                                    />
                                    <CommonButton
                                        type="submit"
                                        title="Save"
                                        style={{ fontSize: "12px" }}
                                        disabled={loading}
                                    />
                                </>
                            )}
                        </div>
                    </form>
                </OffcanvasBody>
            </FlexCol>
        </Offcanvas>
    );
}

export default MultipleMilkEntries;